import type { Messages } from '../en'

export const run: Messages['run'] = {
  // RunView — carregamento / erro / cromo de status
  loadingRun: 'Carregando execução…',
  missingRunId: 'ID da execução ausente.',
  couldNotLoad: 'Não foi possível carregar esta execução.',
  retrievingDelivery: 'Recuperando sua entrega…',
  backToHistory: 'Histórico',
  backToActiveRuns: 'Execuções ativas',
  workingFallback: 'Trabalhando…',
  cancelRun: 'Cancelar execução',
  cancelFailed: 'Falha ao cancelar — a execução ainda está em andamento.',
  pausedReadOnly: 'Esta conversa está pausada aguardando uma resposta. Abra em Execuções ativas para responder.',
  answerAgain: (until: string) =>
    `A sua última resposta não pôde ser concluída. Você pode responder de novo até ${until}.`,
  openToAnswer: 'Abrir para responder',
  awaitingDetailsUnavailable:
    'Esta execução está aguardando sua resposta, mas os detalhes da pergunta não puderam ser carregados. Tente recarregar a página; se persistir, a pausa pode ter expirado.',
  runCancelledNotice: 'Esta execução foi cancelada.',

  // LiveRunView — linha de progresso
  headlineReady: 'Pronto',
  headlineAwaiting: 'Aguardando sua resposta',
  headlineResuming: 'Retomando com sua resposta…',
  headlineCompleted: 'Concluída',
  headlineFailed: 'A execução falhou',
  headlineCancelled: 'Execução cancelada',

  // RunJourney — a linha do tempo reduzida
  historyAria: 'Histórico da execução',
  progressTitle: 'Progresso',
  howItWent: 'Como foi',
  waitingFirstUpdate: 'Aguardando a primeira atualização…',
  truncatedFoot: (n) =>
    n === 1
      ? 'O momento mais antigo foi removido deste histórico.'
      : `Os ${n} momentos mais antigos foram removidos deste histórico.`,

  // RunRequest — "Seu pedido"
  yourRequest: 'Seu pedido',
  noBriefText: 'Nenhum texto de pedido foi fornecido.',
  briefTruncated: 'O pedido armazenado foi truncado para arquivamento.',
  attachmentsCount: (n) => (n === 1 ? '1 anexo' : `${n} anexos`),
  repliesCount: (n) => (n === 1 ? '1 resposta' : `${n} respostas`),
  questionN: (n) => `Pergunta ${n}`,
  agentAskedForInput: 'O agente pediu uma informação sua.',
  yourReplyChip: 'Sua resposta',
  noReplyTimedOut: 'Sem resposta — expirou',
  cancelled: 'Cancelada',

  // RunResult — títulos de resultado
  outcomeAria: 'Resultado da execução',
  errorHeadline: 'Não foi possível concluir esta execução',
  errorFallbackCause: 'Algo deu errado no caminho.',
  errorNoDeliverable: 'Nenhuma entrega foi produzida — você pode iniciar uma nova execução com o mesmo pedido.',
  supportMention: 'Se entrar em contato com o suporte, mencione',
  deliveryAria: 'Sua entrega',
  deliveredWithNotes: 'Entregue — com observações',
  deliveryReady: 'Sua entrega está pronta',
  summaryOfWork: 'Resumo do trabalho',
  finishedEmpty: 'A execução terminou sem mensagem ou arquivos.',
  yourFile: 'Seu arquivo',
  yourFilesCount: (n) => `Seus arquivos (${n})`,
  resultBriefingTitle: 'Relatório do resultado',
  showLess: 'Mostrar menos',
  expandBriefing: 'Expandir relatório',
  downloadFileTitle: (name, size) => `Baixar ${name} (${size})`,

  // RunResult — fatos da faixa de resumo
  finishedAgo: (ago) => `Concluída ${ago}`,
  took: (duration) => `levou ${duration}`,
  filesCount: (n) => (n === 1 ? '1 arquivo' : `${n} arquivos`),
  questionsAnswered: (n) => (n === 1 ? '1 pergunta respondida' : `${n} perguntas respondidas`),
  specialistsInvolved: (n) => (n === 1 ? '1 especialista envolvido' : `${n} especialistas envolvidos`),
  referenceSources: (n) => (n === 1 ? '1 fonte de referência' : `${n} fontes de referência`),

  // RunResult — rótulos de tipo de arquivo (a sigla do selo não é traduzida)
  fileKind: {
    pdf: 'Documento PDF',
    word: 'Documento Word',
    excel: 'Planilha Excel',
    presentation: 'Apresentação',
    zip: 'Arquivo ZIP',
    image: 'Imagem',
    csv: 'Dados CSV',
    json: 'Arquivo de dados',
    markdown: 'Documento',
    text: 'Arquivo de texto',
    generic: 'Arquivo'
  },

  // RunResult — códigos canônicos de problema → linguagem simples
  issues: {
    payloadTooLarge:
      'Alguns arquivos produzidos eram grandes demais para incluir, então esta entrega contém apenas parte deles.',
    engineTimeout: 'O trabalho levou mais tempo do que o permitido e foi interrompido.',
    engineCallerHangup: 'A conexão com o solicitante foi encerrada antes de o trabalho terminar.',
    engineProviderStreamDrop: 'A conexão com o provedor de IA caiu durante a execução.',
    engineToolLoop:
      'O agente insistiu em usar um recurso que não está disponível e não conseguiu concluir. É um problema de configuração do nosso lado, não algo que você fez — tente novamente ou fale com o suporte.',
    userCancelled: 'A execução foi cancelada.'
  },

  // Durações em texto para o cliente (compostas, não compactas)
  durSeconds: (n) => (n === 1 ? '1 segundo' : `${n} segundos`),
  durMinutes: (n) => (n === 1 ? '1 minuto' : `${n} minutos`),
  durHours: (n) => (n === 1 ? '1 hora' : `${n} horas`),
  durDays: (n) => (n === 1 ? '1 dia' : `${n} dias`),
  durDaysHours: (days, hours) =>
    `${days === 1 ? '1 dia' : `${days} dias`} e ${hours === 1 ? '1 hora' : `${hours} horas`}`,
  durHoursMinutes: (h, m) => `${h}h ${m}min`,

  // RunQuestionCard — o cromo da superfície de HITL
  questionCardAria: 'O agente tem uma pergunta para você',
  sendingReply: 'Enviando sua resposta…',
  inputNeeded: 'Precisamos da sua resposta',
  resumingSub: 'A execução continua automaticamente — você pode permanecer nesta página.',
  pausedSub:
    'O trabalho está pausado em uma pergunta que só você pode responder. Ele continua assim que você responder.',
  questionOfRounds: (round, max) => `Pergunta ${round} de ${max}`,
  questionRound: (round) => `Pergunta ${round}`,
  replyWindowClosed: 'Prazo de resposta encerrado',
  replyWithin: (remaining) => `Responda em até ${remaining}`,
  resuming: 'Retomando',
  expiredNotice:
    'O tempo para responder a esta pergunta acabou. A execução será finalizada por conta própria usando premissas documentadas — volte aqui para ver o resultado.',
  resumeError: {
    stale: 'Esta pergunta já foi respondida (talvez em outra aba ou por um colega).',
    notFound: 'Esta pergunta não está mais aberta — a execução pode ter avançado ou expirado.',
    inFlight: 'Sua resposta já está sendo processada — aguarde um instante.',
    auth: 'Sua sessão expirou. Recarregue a página e entre novamente, depois reenvie sua resposta.',
    network: 'Sua resposta não chegou até nós — verifique sua conexão e tente novamente.',
    other: 'Algo deu errado ao enviar sua resposta — tente novamente.'
  },

  // Pacote de textos do SuspensionReplyForm — traduzido para pt-BR
  // (formatBytes é fornecido pelo chamador a partir dos formatadores de i18n).
  replyForm: {
    promptLabel: 'Pergunta',
    rationaleLabel: 'Contexto',
    suggestionsLabel: 'Sugestões',
    prefillTitle: 'Clique para preencher',
    chooseOneLabel: 'Escolha uma opção',
    jsonResponseLabel: 'Resposta (objeto JSON)',
    yourReplyLabel: 'Sua resposta',
    replyPlaceholder: 'Escreva a resposta que o agente está aguardando…',
    responseMustBeJsonObject: 'A resposta deve ser um objeto JSON.',
    replyMustBeJsonObject: 'A resposta deve ser um objeto JSON.',
    attachmentsLabel: 'Anexos',
    attachedNone: '0 anexados',
    attachedSummary: (count, totalBytes, capBytes) =>
      `${count === 1 ? '1 anexado' : `${count} anexados`} · ${totalBytes} / ${capBytes}`,
    attachFiles: 'Anexar arquivos',
    removeAttachment: (name) => `Remover ${name}`,
    perFileCapExceeded: (name, cap) => `"${name}" excede o limite de ${cap} por arquivo.`,
    totalCapExceeded: (total, cap) => `O total de ${total} excederia o limite de ${cap}.`,
    tooManyFiles: (count, max) => `${count} arquivos — no máximo ${max} podem ser anexados.`,
    uploadProgress: (percent) => `${percent}%`,
    uploadFailed: 'Um envio falhou. Tente de novo ou remova o arquivo.',
    retryUpload: (name) => `Tentar enviar ${name} de novo`,
    waitingForUploads: 'Aguardando o envio dos arquivos terminar…',
    submitReply: 'Enviar resposta',
    unknownToolNotice:
      'Esta ferramenta não é reconhecida pela interface. A resposta é encaminhada como parsedCommands, sem alterações.',
    toolInputLabel: 'Entrada da ferramenta',
    replyJsonLabel: 'Resposta (objeto JSON)'
  }
}
